import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { flatMap as _flatMap, get as _get } from 'lodash';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
  delay,
  filter,
  first,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import {
  addVehicleBindQuery,
  AddVehicleCoverage,
  addVehicleInfoQuery,
  addVehicleQuoteQuery,
  AddVehicleTerm
} from '@amfam/policy/add-vehicle/data-access';
import { CoverageOptionModel, CoverageOptionModelWithControl } from '@amfam/policy/add-vehicle/ui';
import { PlautoService } from '@amfam/policy/data-access';
import { AutoPolicy, Coverage, CoverageActions } from '@amfam/policy/models';
import {
  addBaseToSelectedVehicleCoverage,
  deleteVehicleCoverages,
  FEATURES,
  VehicleCoveragesFacade
} from '@amfam/policy/vehicle-coverages/data-access';
import { vehicleQuery } from '@amfam/policy/vehicle/data-access';
import { QuoteModel } from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService, UtilService } from '@amfam/shared/utility/shared-services';
import { LoadingSpinnerService } from '@amfam/ui-kit';

import * as VehicleInfoActions from '../+state/add-vehicle-feature.actions';
import { loadFuturePaymemts } from '../+state/add-vehicle-feature.actions';
import * as addVehicleFeatureSelector from '../+state/add-vehicle-feature.selectors';
import { CoverageDescriptionModel, CoverageDescriptionModelForPartnerSpecific } from '../models';
import { CoveragesModel, PolicyCoverages } from '../models/coverages.model';

@Component({
  selector: 'ds-vehicle-quote-info',
  templateUrl: './vehicle-quote-info.component.html',
  styleUrls: [
    './vehicle-quote-info.component.scss',
    '../add-vehicle-container/add-vehicle-container.component.scss'
  ],
  standalone: false
})
export class VehicleQuoteInfoComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() cancelEvent = new EventEmitter<boolean>();
  @Output() errorEvent = new EventEmitter<{ hasError: boolean; errorMessage: string }>();
  @Output() notifyAgentEvent = new EventEmitter<{ isProcessExited: boolean }>();

  public authorized$ = this.roleService.isAuthorized('addvehicle_sendtoagent');
  public isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
  public userState$ = this.store.select(userQuery.selectUserStateCode);
  public UMPDStateCodes: string;
  stateCode: string;

  public driversList$ = this.store.select(addVehicleInfoQuery.selectNewVehicleDrivers);

  public driverNamesList$ = this.driversList$.pipe(
    map(driverList => _flatMap(driverList, driver => driver.name))
  );
  public vehicleRiskDescription$ = this.store.select(vehicleQuery.selectNewRiskDescription);

  public vehicleRiskDescriptionWithoutSeries$ = this.store.select(
    vehicleQuery.selectNewRiskDescriptionWithoutSeries
  );

  public currentVehicleRiskDescriptionInfoMessage$ = this.store
    .select(addVehicleInfoQuery.selectCoveragesModelledAfter)
    .pipe(
      map(riskDescription =>
        this.copyService.getCopy('policy.addVehicleCustomizeCoverageInfo', {
          currentVehicleInfo: riskDescription
        })
      )
    );

  public currentVehicleRiskDescription$ = this.isPartner$.pipe(
    withLatestFrom(
      this.store.select(addVehicleFeatureSelector.selectCurrentVehicleMappedFromInfo),
      this.vehicleCoveragesFacade.vehicleName$
    ),
    map(([isPartner, afiVehicleName, vehicleName]) => (isPartner ? vehicleName : afiVehicleName))
  );

  public quoteDataForSideBar$ = this.store.select(addVehicleQuoteQuery.selectNewVehicleRate).pipe(
    filter(quote => !!quote),
    map(
      (quoteRate): QuoteModel => ({
        quoteMonthlyDollars: this.utilService.returnQuoteDollarOrCents(
          quoteRate.monthlyPremium,
          'Dollar'
        ),
        quoteMonthlyCents: this.utilService.returnQuoteDollarOrCents(
          quoteRate.monthlyPremium,
          'Cent'
        ),
        quoteTerm: quoteRate.termMonths,
        quoteTotalAmount: Number(quoteRate.totalCost.toFixed(2))
      })
    ),
    delay(0),
    tap(() => {
      this.handleInitialDictionaryValueAssignment(Object.keys(this.initialValue).length === 0);
      this.quoteInfoForm.enable();
    })
  );

  public quoteDataForSideBarPartner$ = this.vehicleCoveragesFacade.rate$.pipe(
    filter(quote => !!quote),
    map(
      (quoteRate): QuoteModel => ({
        quoteMonthlyDollars: this.utilService.returnQuoteDollarOrCents(
          +quoteRate.monthlyPremium,
          'Dollar'
        ),
        quoteMonthlyCents: this.utilService.returnQuoteDollarOrCents(
          +quoteRate.monthlyPremium,
          'Cent'
        ),
        quoteTerm: quoteRate.termMonths,
        quoteTotalAmount: Number(quoteRate.totalCost.toFixed(2))
      })
    ),
    delay(0),
    tap(() => {
      this.handleInitialDictionaryValueAssignment(Object.keys(this.initialValue).length === 0);
      this.quoteInfoForm.enable();
    })
  );

  public quoteInfoForm: UntypedFormGroup = this.formBuilder.group({});

  extraTermsAvailableCoverageCodes = this.copyService.getCopy(
    'policy.extraTermsAvailableCoverageCodes'
  );
  extraTermsAvailableCoverageCodeDescription = this.copyService.getCopy(
    'policy.extraTermsAvailableCoverageCodeDescription'
  );

  public partnerCoverages$: Observable<CoverageOptionModelWithControl[]> = this.store
    .select(addVehicleFeatureSelector.selectCoverages)
    .pipe(map(coverages => coverages));

  noOfVehicles$ = this.store.select(addVehicleInfoQuery.selectNumberOfVehicles).pipe(
    withLatestFrom(this.isPartner$),
    map(([count]) => count)
  );

  icons$ = this.noOfVehicles$.pipe(
    map(count => (count ? (count <= 3 ? Array(count).fill(1) : [1]) : []))
  );

  public vehicleAppliedDiscountsName$: Observable<string[]> = this.store
    .select(addVehicleQuoteQuery.selectNewVehicleAppliedDiscounts)
    .pipe(
      map(appliedDiscounts =>
        _flatMap(appliedDiscounts, appliedDiscount => appliedDiscount.name.replace(/Discount/, ''))
      )
    );

  public vehicleAppliedDiscountsNamePartner$: Observable<string[]> =
    this.vehicleCoveragesFacade.appliedDiscounts$.pipe(
      map(appliedDiscounts =>
        _flatMap(appliedDiscounts, appliedDiscount => appliedDiscount.name.replace(/Discount/, ''))
      )
    );

  public hasAntiTheftDiscount$: Observable<boolean> = this.store
    .select(addVehicleQuoteQuery.selectNewVehicleAppliedDiscounts)
    .pipe(
      map(appliedDiscounts =>
        appliedDiscounts
          ? !!appliedDiscounts.find(appliedDiscount =>
              this.isAntiTheftDiscountCode(appliedDiscount)
            )
          : false
      )
    );

  public initialValueDictionaryAfterRecalculate: Dictionary<string> = {};
  initialValue: Dictionary<string> = {};

  public needRecalculation$: Observable<boolean> = this.buildStatusFor(CoverageActions.RECALCULATE);
  showResetCoverageButton$: Observable<boolean> = this.buildStatusFor(
    CoverageActions.RESETCOVERAGE
  );

  public deductibleHeader$ = this.isPartner$.pipe(
    map(isPartner =>
      this.copyService.getCopy(
        isPartner
          ? 'policy.addVehicleDeductibleHeader'
          : 'policy.addVehicleDeductibleHeaderWithVehicleInfo'
      )
    )
  );
  public includedCoveragesHeader = this.copyService.getCopy(
    'policy.addVehicleIncludedCoverageHeader'
  );
  public discountsHeader = this.copyService.getCopy('policy.addVehicleDiscountsHeader');

  public disableNextbtn = false;

  private isEnrolledInKYDWithTPMPackage$ = this.store.select(
    addVehicleInfoQuery.selectHasTPMPackageAvailibility
  );

  private hasTPMOnPolicyWithRequiredERSRTAA$ = this.store.select(
    addVehicleQuoteQuery.selectHasTPMPackageAndERSRTAARequired,
    {
      coveragesCodeList: Object.keys(
        this.copyService.getCopy('policy.kydTPMPackageUnModifiableCoverages')
      ),
      TPMCoverageCode: this.copyService.getCopy('policy.travelPeaceOfMindCoverageCode')
    }
  );

  private hasCostcoExecutiveMembership$ = this.store.select(
    addVehicleInfoQuery.selectHasCostcoExecutiveMembership
  );

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public vehicleCoverages$: Observable<CoverageOptionModel[]> = this.store
    .select(addVehicleQuoteQuery.selectNewVehicleCoverages)
    .pipe(
      withLatestFrom(
        this.store.select(userQuery.selectUserStateCode),
        this.isEnrolledInKYDWithTPMPackage$,
        this.hasTPMOnPolicyWithRequiredERSRTAA$,
        this.isPartner$,
        this.hasCostcoExecutiveMembership$,
        this.store.select(addVehicleInfoQuery.selectHasCostcoGoldStarMembership)
      ),
      map(
        ([
          coverages,
          stateCode,
          isEnrolledInKYDTPM,
          hasTPMOnPolicyWithRequiredERSRTAA,
          isPartner,
          hasCostcoExecutiveMembership,
          hasCostcoGoldStarMembership
        ]) => {
          if (isPartner) {
            return [];
          }

          const updatedCoverages = [...coverages];
          const coverageCodeListFromCopy = Object.keys(this.coverageCopyObjList);
          const coverageCodeListFromData = _flatMap(coverages, coverage => coverage.publicID);
          for (const copyCoverageCode of coverageCodeListFromCopy) {
            if (!coverageCodeListFromData.find(coverageCode => coverageCode === copyCoverageCode)) {
              updatedCoverages.push({
                coverageCode: copyCoverageCode,
                coverageName: '',
                termValue: '',
                selected: false,
                required: false,
                hasTerms: false,
                terms: []
              });
            }
          }

          return updatedCoverages
            .filter(coverage => {
              const coverageCopyObj = this.coverageCopyObjList[_get(coverage, 'publicID')];
              const eligibleStates = _get(coverageCopyObj, 'eligibleStates', []);
              const eligbleForCoverageInState =
                eligibleStates.length > 0
                  ? !!eligibleStates.find(eligibleState => eligibleState === stateCode)
                  : true;
              return !!coverageCopyObj && eligbleForCoverageInState;
            })
            .filter(
              coverage =>
                !(
                  (isEnrolledInKYDTPM || hasTPMOnPolicyWithRequiredERSRTAA) &&
                  this.copyService.getCopy('policy.kydTPMPackageUnModifiableCoverages')[
                    coverage.publicID
                  ]
                )
            )
            .filter(coverage =>
              isPartner && hasCostcoExecutiveMembership
                ? _get(coverage, 'publicID') !== 'PAEmergencyRoadServiceCov_af'
                : true
            )
            .map((coverage): CoverageOptionModel => {
              const coverageDescriptionObj = this.coverageCopyObjList[_get(coverage, 'publicID')];
              const coverageOptions = _flatMap(coverage.terms, term => ({
                options: _flatMap(_get(term, 'options'), option => option.name),
                selectedValue: coverage.selected ? _get(term, 'chosenTermValue') : ''
              }));

              return {
                isDisabled:
                  hasCostcoGoldStarMembership && stateCode === this.stateCode ? true : false,
                coverageCode: _get(coverage, 'publicID'),
                coverageDescription: coverage.description,
                coverageSubHeading: _get(coverageDescriptionObj, 'subHeading'),
                kydTPMPackageCoverageInfo: isEnrolledInKYDTPM
                  ? _get(coverageDescriptionObj, 'KYDTPMPackageInfo')
                  : '',
                options: _get(coverageOptions[0], 'options'),
                selectedValue: _get(coverageDescriptionObj, 'isCheckBox')
                  ? coverage.selected
                  : this.buildSelectedOption(
                      coverageOptions,
                      coverageDescriptionObj,
                      isEnrolledInKYDTPM || hasTPMOnPolicyWithRequiredERSRTAA,
                      0
                    ),
                deductibleDictionary: this.handleUninsuredDeductibleDictionaryAssignement(
                  coverage,
                  coverageOptions
                ),
                coverageHelpTextList: this.handleUnderInsuredDeductibleHelpText(coverage),
                isCheckbox: !!_get(coverageDescriptionObj, 'isCheckBox')
              };
            })
            .sort((x, y) => (x.isCheckbox === y.isCheckbox ? 0 : x.isCheckbox ? 1 : -1));
        }
      )
    );

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public policyCoverages$: Observable<PolicyCoverages> = this.store
    .select(addVehicleFeatureSelector.selectCurrentAutoPolicy)
    .pipe(
      map(currentAutoPolicy =>
        this.autoPolicyService.getPolicyLevelCoverages(<AutoPolicy>currentAutoPolicy)
      ),
      withLatestFrom(
        this.isEnrolledInKYDWithTPMPackage$,
        this.hasTPMOnPolicyWithRequiredERSRTAA$,
        this.hasCostcoExecutiveMembership$,
        this.isPartner$,
        this.store.select(userQuery.selectUserStateCode)
      ),
      map(
        ([
          coverages,
          isEnrolledInKYDTPM,
          hasTPMOnPolicyWithRequiredERSRTAA,
          hasCostcoExecutiveMembership,
          isPartner,
          stateCode
        ]) => {
          if (isEnrolledInKYDTPM || hasTPMOnPolicyWithRequiredERSRTAA) {
            coverages.push(...this.buildPolicyLevelCoveragesForKYDTPMPackage());
          }

          if (isPartner && hasCostcoExecutiveMembership) {
            const coverageDescriptionObj =
              this.coverageCopyObjListForPartnerSpecific['PAEmergencyRoadServiceCov_af'];
            const description = _get(coverageDescriptionObj, `description.${stateCode}`)
              ? _get(coverageDescriptionObj, `description.${stateCode}`)
              : _get(coverageDescriptionObj, 'description.Default');
            coverages.push({
              name: description,
              description: description,
              coverageType: '',
              coverageLevel: '',
              code: 'PAEmergencyRoadServiceCov_af',
              rejectedCoverage: false,
              appliesToMultipleRisks: false,
              deductibleTerms: [],
              limitTerms: [],
              otherTerms: []
            });
          }

          return coverages;
        }
      ),

      map(coverages => {
        const coverageModels: CoveragesModel[] = [];
        const additionalCoverages: string[] = [];
        for (const coverage of coverages) {
          if (
            _get(coverage, 'limitTerms[0].value') !== '' ||
            _get(coverage, 'deductibleTerms[0].value') !== ''
          ) {
            const coverageMap = this.copyService.getCopy('policy.policyCoverageCodeMap');
            const code = coverageMap[coverage.code];
            const subHeading = code ? code['subHeading'] : '';
            const coverageModel = new CoveragesModel(coverage, subHeading);
            if (coverageModel.isVisible) {
              coverageModels.push(coverageModel);
            }
          } else if (
            _get(coverage, 'otherTerms[0].valueCode') !== 'Rejected' &&
            !_get(coverage, 'rejectedCoverage')
          ) {
            // Only display additional coverages if customer has not Rejected them
            additionalCoverages.push(coverage.description);
          }
        }
        return {
          coverages: coverageModels,
          additionalCoverages: additionalCoverages
        };
      })
    );

  private coverageCopyObjListForPartnerSpecific = <
    Dictionary<CoverageDescriptionModelForPartnerSpecific>
  >this.copyService.getCopy('policy.vehicleCoverageCodeMapForPartners');

  private coverageCopyObjList = <Dictionary<CoverageDescriptionModel>>(
    this.copyService.getCopy('policy.vehicleCoverageCodeMap')
  );

  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private spinnerService: LoadingSpinnerService,
    private copyService: CopyService,
    private autoPolicyService: PlautoService,
    private utilService: UtilService,
    public roleService: ImpersonateRolesService,
    private featureFlagService: FeatureFlagService,
    public vehicleCoveragesFacade: VehicleCoveragesFacade
  ) {}

  ngOnInit(): void {
    this.store.select(userQuery.selectUserStateCode).subscribe(ele => {
      this.UMPDStateCodes = ele;
    });
    this.spinnerService.stop();
    this.handleError();
    this.handleLoadingSpinner();
    this.stateCode = this.copyService.getCopy('policy.stateCodeMassachusetts');
  }

  saveAndRate() {
    this.vehicleCoveragesFacade.saveAndRate(FEATURES.ADD);
    this.setToNavigateToFuturePaymentSchedule(false);
  }

  ngAfterViewInit(): void {
    this.store.dispatch(
      VehicleInfoActions.addVehicleSendAnalytics({
        payload: { pageAnalyticName: 'CustomizeCoverage' }
      })
    );
  }

  updateCoverage(payload: { coverage: CoverageOptionModelWithControl; value: string }) {
    this.isPartner$
      .pipe(
        first(),
        tap(isPartner => {
          const optOutCoverages = isPartner
            ? this.copyService.getCopy('policy.optOutforPartnerCoverages')
            : [];
          this.store.dispatch(
            VehicleInfoActions.updateCoverage({ ...payload, optOutCoverages: optOutCoverages })
          );
        })
      )
      .subscribe();
  }

  hasExtraOptions(coverage: CoverageOptionModel): boolean {
    return coverage?.extraOptions?.length > 0;
  }

  recalculateQuote() {
    this.spinnerService.start({
      blockActions: true,
      isProgressBar: true,
      secondaryMessage: this.copyService.getCopy('policy.quoteCalculatingLoadingSpinnerText'),
      isRecalculate: true
    });

    this.setToNavigateToFuturePaymentSchedule(false);

    this.buildAndSetCoverages(true);

    this.store.dispatch(
      VehicleInfoActions.addVehicleSendAnalytics({
        payload: { buttonAnalyticName: 'CustomizeCoverage:Recalculate' }
      })
    );
  }

  public buildAndSetCoverages(dispatchRate = false) {
    this.isPartner$
      .pipe(
        take(1),
        switchMap(isPartner =>
          this.buildVehicleCoverages(isPartner).pipe(
            tap(vehicleCoveragesSelectedInForm => {
              this.setVehicleCoverages(vehicleCoveragesSelectedInForm);
              if (dispatchRate) {
                if (isPartner) {
                  this.vehicleCoveragesFacade.saveAndRate(FEATURES.ADD);
                } else {
                  this.store.dispatch(VehicleInfoActions.addVehicleRate());
                }
              }
            })
          )
        )
      )
      .subscribe();
  }

  reset() {
    this.isPartner$
      .pipe(
        first(),
        tap(isPartner => (isPartner ? this.resetQuoteForPartner() : this.resetQuote()))
      )
      .subscribe();
  }

  resetQuoteForPartner() {
    this.vehicleCoveragesFacade.baseEntity$
      .pipe(
        withLatestFrom(
          this.vehicleCoveragesFacade.idsExceptBaseId$,
          this.vehicleCoveragesFacade.vehicleId$
        ),
        filter(([baseEntity, ids, vehicleId]) => !!baseEntity && !!ids && !!vehicleId),
        first(),
        tap(([baseEntity, ids, vehicleId]) => {
          // this should be moved to the facade - justin
          this.store.dispatch(deleteVehicleCoverages({ ids }));
          // this should be moved to the facade - justin
          this.store.dispatch(
            addBaseToSelectedVehicleCoverage({
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              payload: { ...baseEntity, id: uuid() } as any,
              feature: FEATURES.ADD,
              vehicleId: vehicleId.toString()
            })
          );
        })
      )
      .subscribe();
  }

  resetQuote() {
    /**
     * AS : If we already have coverages set in the features store that indicates customer has
     * tried to recalculate more than once and to get to the first state we would need a rate call.
     */

    this.store
      .select(addVehicleFeatureSelector.selectCurrentVehicleInfo)
      .pipe(
        map(currentVehicleInfo => currentVehicleInfo.vehicleCoverages),
        take(1)
      )
      .subscribe(vehicleCurrentCoverages => {
        if (vehicleCurrentCoverages) {
          this.spinnerService.start({
            blockActions: true
          });

          this.store.dispatch(VehicleInfoActions.addVehicleResetRate());
        } else {
          for (const initialItemKey of Object.keys(this.initialValueDictionaryAfterRecalculate)) {
            this.quoteInfoForm.controls[initialItemKey].setValue(
              this.initialValueDictionaryAfterRecalculate[initialItemKey]
            );
          }
        }
      });

    this.store.dispatch(
      VehicleInfoActions.addVehicleSendAnalytics({
        payload: { buttonAnalyticName: 'CustomizeCoverage:ResetCoverage' }
      })
    );
  }

  nextStep() {
    of(this.quoteInfoForm.value)
      .pipe(
        withLatestFrom(this.store.select(addVehicleQuoteQuery.selectNewVehicleRateError)),
        switchMap(([changes, vehicleFeaturesError]) =>
          this.checkFormAndErrorValues(changes, vehicleFeaturesError)
        ),
        withLatestFrom(this.isPartner$),
        take(1)
      )
      .subscribe(([needRecalculation, isPartner]) => {
        if (needRecalculation) {
          this.setToNavigateToFuturePaymentSchedule(true);

          this.spinnerService.start({
            blockActions: true,
            secondaryMessage: this.copyService.getCopy('policy.quoteCalculatingLoadingSpinnerText')
          });

          this.buildVehicleCoverages(isPartner).subscribe(vehicleCoveragesSelectedInForm => {
            this.setVehicleCoverages(vehicleCoveragesSelectedInForm);
            if (isPartner) {
              this.vehicleCoveragesFacade.saveAndRate(FEATURES.ADD);
            } else {
              this.store.dispatch(VehicleInfoActions.addVehicleRate());
            }
          });
        } else {
          this.spinnerService.start({
            blockActions: true
          });
          this.buildAndSetCoverages();
          this.setToNavigateToFuturePaymentSchedule(true);
          this.store.dispatch(loadFuturePaymemts());
        }
      });
  }

  goBackOneStep() {
    this.store.dispatch(fromRouterActions.Back());
  }

  ngOnDestroy() {
    this.spinnerService.stop();
    this.stop$.next();
    this.stop$.complete();
  }

  private handleError() {
    combineLatest([
      this.store.select(addVehicleQuoteQuery.selectNewVehicleRateError),
      this.store.select(addVehicleBindQuery.selectVehicleANSNotificationError),
      this.isPartner$
    ])
      .pipe(
        map(([rateError, ansError, isPartner]) => ({ rateError, ansError, isPartner })),
        takeUntil(this.stop$)
      )
      .subscribe(({ rateError, ansError, isPartner }) => {
        if (rateError || ansError) {
          this.errorEvent.emit();
          this.spinnerService.stop();
          this.disableNextbtn = true;
        } else {
          this.errorEvent.emit({
            hasError: false,
            errorMessage: this.copyService.getCopy(
              isPartner
                ? 'policy.addVehicleRecalculateErrorMessageForPartner'
                : 'policy.addVehicleRecalculateErrorMessage'
            )
          });
          this.disableNextbtn = false;
        }
      });
  }

  private handleLoadingSpinner() {
    this.store
      .select(addVehicleQuoteQuery.selectVehicleQuoteLoading)
      .pipe(
        withLatestFrom(this.store.select(addVehicleFeatureSelector.selectShouldLoadFuturePayments)),
        takeUntil(this.stop$)
      )
      .subscribe(([vehicleQuoteLoading, waitForFuturePayments]) => {
        if (!vehicleQuoteLoading && !waitForFuturePayments) this.spinnerService.stop();
      });
  }

  private handleInitialDictionaryValueAssignment(init = false) {
    for (const control in this.quoteInfoForm.controls) {
      if (control) {
        this.initialValueDictionaryAfterRecalculate[control] =
          this.quoteInfoForm.controls[control].value;
        if (init) this.initialValue[control] = this.quoteInfoForm.controls[control].value;
      }
    }
  }

  private handleUninsuredDeductibleDictionaryAssignement(
    coverage: AddVehicleCoverage,
    coverageOptions: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      options: any[];
      selectedValue: string;
    }[]
  ) {
    const uninsuredMotoristPropertyDamageDeductibleDictionary: Dictionary<string> = {};
    if (
      _get(coverageOptions, 'length', 0) > 0 &&
      _get(coverage, 'publicID') ===
        this.copyService.getCopy('policy.uninsuredMotoristPropertyDamage')
    ) {
      for (let i = 0; i < coverageOptions[0].options.length; i++) {
        uninsuredMotoristPropertyDamageDeductibleDictionary[coverageOptions[0].options[i]] =
          this.getDeductibleUMPD(coverageOptions[0].options[i], coverageOptions[1].options);
      }
    }
    return uninsuredMotoristPropertyDamageDeductibleDictionary;
  }

  private getDeductibleUMPD(umpdCoverageOption: string, deductibleOptions: string[]) {
    if (umpdCoverageOption.toLowerCase() === 'rejected') {
      return deductibleOptions.find(option => option.toLowerCase() === 'no coverage');
    }
    return deductibleOptions.find(option => option.toLowerCase() !== 'no coverage');
  }

  private handleUnderInsuredDeductibleHelpText(coverage: AddVehicleCoverage) {
    const underInsuredHelpTextObj: string[] = [];

    for (const term of coverage.terms) {
      if (term.name === this.copyService.getCopy('policy.hit-and-RunPhantomVehicleDamage')) {
        underInsuredHelpTextObj.push(
          this.copyService.getCopy(
            `policy.vehicleCoverageCodeMap[${coverage.publicID}]['coverageHelpText1']`,
            {
              hitdeductible: term.chosenTermValue
            }
          )
        );
      } else if (
        term.name === this.copyService.getCopy('policy.notAHit-and-RunPhantomVehicleDamage')
      ) {
        underInsuredHelpTextObj.push(
          this.copyService.getCopy(
            `policy.vehicleCoverageCodeMap[${coverage.publicID}]['coverageHelpText2']`,
            {
              noHitdeductible: term.chosenTermValue
            }
          )
        );
      }
    }

    return underInsuredHelpTextObj;
  }
  /**
   * @description
   * @date 19/10/2021
   * @private
   * @param {AddVehicleCoverage} matchedCoverage
   * @param {string} initialItemKey
   * @returns {*}
   * @memberof VehicleQuoteInfoComponent
   */
  private handleTermsAndDeductibleUpdationForRecalculate(
    matchedCoverage: AddVehicleCoverage,
    initialItemKey: string
  ) {
    matchedCoverage.terms[0] = Object.assign({}, matchedCoverage.terms[0], {
      chosenTermValue: this.quoteInfoForm.controls[initialItemKey].value
    });
    switch (matchedCoverage.publicID) {
      case this.copyService.getCopy('policy.underinsuredMotoristPropertyDamageCode'):
        return Object.assign({}, matchedCoverage, {
          termValue: this.quoteInfoForm.controls[initialItemKey].value,
          selected: !!this.quoteInfoForm.controls[initialItemKey].value,
          terms: matchedCoverage.terms
        });

      case this.copyService.getCopy('policy.uninsuredMotoristPropertyDamage'):
        if (this.UMPDStateCodes !== 'IL') {
          matchedCoverage.terms[1] = Object.assign({}, matchedCoverage.terms[1], {
            chosenTermValue: this.getCoverageTermsForUMPD(
              this.quoteInfoForm.controls[initialItemKey].value,
              matchedCoverage.terms
            )?.name
          });
        }

        return Object.assign({}, matchedCoverage, {
          termValue: this.quoteInfoForm.controls[initialItemKey].value,
          selected: !!this.quoteInfoForm.controls[initialItemKey].value,
          terms: matchedCoverage.terms
        });

      default:
        /**
         * Return something when there's no match here. (This may need to be refined)
         */
        return {
          ...matchedCoverage,
          termValue: this.quoteInfoForm.controls[initialItemKey].value,
          selected: !!this.quoteInfoForm.controls[initialItemKey].value,
          terms: matchedCoverage.terms
        };
    }
  }

  private getCoverageTermsForUMPD(umpdSelectedValue: string, terms: AddVehicleTerm[]) {
    if (umpdSelectedValue.toLowerCase() === 'rejected') {
      return terms[1].options.find(option => option.name.toLowerCase() === 'no coverage');
    }
    return terms[1].options.find(option => option.name.toLowerCase() !== 'no coverage');
  }

  private handleSingleTermAssignmentForRecalculatePAEmergencyRoadServiceCov(
    matchedCoverage: AddVehicleCoverage,
    initialItemKey: string
  ) {
    return Object.assign({}, matchedCoverage, {
      selected: !!this.quoteInfoForm.controls[initialItemKey].value
    });
  }

  private handleSingleTermAssignmentForRecalculatePAPropertyDamageCov(
    matchedCoverage: AddVehicleCoverage,
    initialItemKey: string
  ) {
    return Object.assign({}, matchedCoverage, {
      selected: !!this.quoteInfoForm.controls[initialItemKey].value
    });
  }

  private handleTermsAndDeductibleUpdationOfExtraTermsForRecalculate(
    matchedCoverage: AddVehicleCoverage,
    initialItemKey: string
  ) {
    matchedCoverage.terms[0] = Object.assign({}, matchedCoverage.terms[0], {
      chosenTermValue: this.quoteInfoForm.controls[initialItemKey].value
    });

    matchedCoverage.terms[1] = Object.assign({}, matchedCoverage.terms[1], {
      chosenTermValue: this.quoteInfoForm.controls[`${initialItemKey}`].value
    });

    return Object.assign({}, matchedCoverage, {
      termValue: this.quoteInfoForm.controls[`${initialItemKey}`].value,
      selected: !!this.quoteInfoForm.controls[`${initialItemKey}`].value,
      terms: matchedCoverage.terms
    });
  }

  private handleSingleTermAssignmentForRecalculate(
    matchedCoverage: AddVehicleCoverage,
    initialItemKey: string,
    selectedOption?
  ) {
    return Object.assign({}, matchedCoverage, {
      termValue: this.quoteInfoForm.controls[initialItemKey].value,
      selected: !!this.quoteInfoForm.controls[initialItemKey].value,
      updated: !!this.quoteInfoForm.controls[initialItemKey].value,
      terms: [
        Object.assign({}, matchedCoverage.terms[0], {
          chosenTermValue: this.quoteInfoForm.controls[initialItemKey].value,
          chosenTerm: selectedOption.filter(
            s => s.name === this.quoteInfoForm.controls[initialItemKey].value
          )[0]?.code,
          updated: !!this.quoteInfoForm.controls[initialItemKey].value
        })
      ]
    });
  }

  private buildPolicyLevelCoveragesForKYDTPMPackage() {
    const coveragesToBeMovedToPolicySection = Object.keys(
      <Dictionary<string>>this.copyService.getCopy('policy.kydTPMPackageUnModifiableCoverages')
    );

    const coverageList: Coverage[] = [];

    for (const coverageCode of coveragesToBeMovedToPolicySection) {
      coverageList.push({
        name: this.coverageCopyObjList[coverageCode].description,
        description: this.coverageCopyObjList[coverageCode].description,
        coverageType: '',
        coverageLevel: '',
        code: coverageCode,
        rejectedCoverage: false,
        appliesToMultipleRisks: false,
        deductibleTerms: [],
        limitTerms: [],
        otherTerms: []
      });
    }

    return coverageList;
  }

  private buildSelectedOption(
    coverageOptions: {
      options: string[];
      selectedValue: string;
    }[],
    coverageDescriptionObj: CoverageDescriptionModel | CoverageDescriptionModelForPartnerSpecific,
    isEnrolledInKYDTPM: boolean,
    index: number
  ) {
    return _get(coverageOptions[index], 'selectedValue')
      ? _get(coverageOptions[index], 'selectedValue')
      : isEnrolledInKYDTPM
        ? _get(coverageDescriptionObj, 'kydPackageDefaultSelectedoption', '')
        : '';
  }

  private setToNavigateToFuturePaymentSchedule(navigate: boolean) {
    this.store.dispatch(
      VehicleInfoActions.addVehicleNavigateToFuturePaymentSchedule({
        navigateToFuturePaymentSchedule: navigate
      })
    );
  }

  private isAntiTheftDiscountCode(appliedDiscount) {
    return appliedDiscount.discountCode === 'PAAntiTheftDisc_af';
  }

  private setVehicleCoverages(vehicleCoverages: AddVehicleCoverage[]) {
    this.store.dispatch(
      VehicleInfoActions.setVehicleCoveragesInfo({
        vehicleCoverages: vehicleCoverages
      })
    );
  }

  private buildVehicleCoverages(isPartner = false): Observable<AddVehicleCoverage[]> {
    return this.store.select(addVehicleQuoteQuery.selectNewVehicleCoverages).pipe(
      take(1),
      withLatestFrom(
        this.vehicleCoverages$,
        this.partnerCoverages$,
        this.store.select(userQuery.selectUserStateCode)
      ),
      map(([vehicleCoveragesFromInitiate, vehicleCoverages, partnerCoverages, userStateCode]) => {
        const alreadyPreparedItems = [];
        const vehicleCoveragesSelectedInForm: AddVehicleCoverage[] = [];
        for (let initialItemKey of Object.keys(this.initialValueDictionaryAfterRecalculate)) {
          if (initialItemKey.includes(':')) {
            initialItemKey = initialItemKey.split(':')[0];
            if (!alreadyPreparedItems.includes(initialItemKey.split(':')[0])) {
              alreadyPreparedItems.push(initialItemKey);
            } else {
              continue;
            }
          }
          let matchedCoverage = vehicleCoveragesFromInitiate.find(
            vehicleCoverageFromInitiate => vehicleCoverageFromInitiate.publicID === initialItemKey
          );
          const selectedOptionKey =
            matchedCoverage?.terms.length > 0 ? matchedCoverage.terms[0].options : '';
          if (!matchedCoverage) {
            const coverageDisplayedWithComingFromInitiate = !isPartner
              ? vehicleCoverages.find(
                  vehicleCoverageDisplayed =>
                    vehicleCoverageDisplayed.coverageCode === initialItemKey
                )
              : partnerCoverages.find(
                  vehicleCoverageDisplayed =>
                    vehicleCoverageDisplayed.coverageCode === initialItemKey
                );

            if (coverageDisplayedWithComingFromInitiate) {
              matchedCoverage = {
                coverageCode: coverageDisplayedWithComingFromInitiate.coverageCode,
                coverageName: coverageDisplayedWithComingFromInitiate.coverageDescription,
                hasTerms: true,
                required: false,
                selected: true,
                terms: [],
                termValue: ''
              };
            }
          }

          if (matchedCoverage) {
            if (
              isPartner &&
              this.extraTermsAvailableCoverageCodes[userStateCode] &&
              this.extraTermsAvailableCoverageCodes[userStateCode].includes(initialItemKey) &&
              _get(matchedCoverage, 'terms.length', 0) > 1
            ) {
              vehicleCoveragesSelectedInForm.push(
                this.handleTermsAndDeductibleUpdationOfExtraTermsForRecalculate(
                  matchedCoverage,
                  initialItemKey
                )
              );
            } else if (
              _get(matchedCoverage, 'terms.length', 0) === 0 ||
              initialItemKey === 'PAEmergencyRoadServiceCov_af'
            ) {
              vehicleCoveragesSelectedInForm.push(
                Object.assign({}, matchedCoverage, {
                  selected: !!this.quoteInfoForm.controls[initialItemKey].value
                })
              );
            } else if (_get(matchedCoverage, 'terms.length') === 1) {
              if (initialItemKey === 'PAEmergencyRoadServiceCov_af') {
                vehicleCoveragesSelectedInForm.push(
                  this.handleSingleTermAssignmentForRecalculatePAEmergencyRoadServiceCov(
                    matchedCoverage,
                    initialItemKey
                  )
                );
              } else {
                vehicleCoveragesSelectedInForm.push(
                  this.handleSingleTermAssignmentForRecalculate(
                    matchedCoverage,
                    initialItemKey,
                    selectedOptionKey
                  )
                );
              }
            } else if (_get(matchedCoverage, 'terms.length') > 1) {
              if (initialItemKey === 'PAUmpdCov_af' && this.UMPDStateCodes === 'IL') {
                vehicleCoveragesSelectedInForm.push(
                  this.handleSingleTermAssignmentForRecalculatePAPropertyDamageCov(
                    matchedCoverage,
                    initialItemKey
                  )
                );
              } else {
                vehicleCoveragesSelectedInForm.push(
                  this.handleTermsAndDeductibleUpdationForRecalculate(
                    matchedCoverage,
                    initialItemKey
                  )
                );
              }
            }
          }
        }
        return vehicleCoveragesSelectedInForm;
      })
    );
  }

  private buildStatusFor(action = CoverageActions.RECALCULATE): Observable<boolean> {
    return combineLatest(
      this.quoteInfoForm.valueChanges,
      this.store.select(addVehicleQuoteQuery.selectNewVehicleRateError)
    ).pipe(
      mergeMap(
        ([changes, vehicleFeaturesError]): Observable<boolean> =>
          action === CoverageActions.RESETCOVERAGE
            ? this.checkFormAndErrorValues(changes, vehicleFeaturesError, true)
            : this.checkFormAndErrorValues(changes, vehicleFeaturesError)
      )
    );
  }
  private checkFormAndErrorValues(
    changes,
    vehicleFeaturesError,
    forReset = false
  ): Observable<boolean> {
    let returnVal;
    const initialValue = forReset ? this.initialValue : this.initialValueDictionaryAfterRecalculate;
    for (const change of Object.keys(changes)) {
      if (changes[change] === false && initialValue[change] === '') {
        returnVal = false;
      } else if (changes[change] !== initialValue[change]) {
        returnVal = true;
        break;
      } else {
        returnVal = false;
      }
    }
    return of(!!vehicleFeaturesError || returnVal);
  }
}
